import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { fetchMyPlatformFeeStatus, FOUNDING_LISTER_CAP_DEFAULT } from './platformFees';

/**
 * Marketing banner: first N listers keep 100% for life.
 * Hides when offer is full, user already free-for-life, or still loading.
 */
function FoundingListerBanner({ user, compact = false, className = '' }) {
  const [promo, setPromo] = useState(null);
  const [alreadyExempt, setAlreadyExempt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const promoCallable = httpsCallable(functions, 'getFoundingListerPromo');
        const promoPromise = promoCallable({});
        const statusPromise = user ? fetchMyPlatformFeeStatus().catch(() => null) : Promise.resolve(null);
        const [promoResult, status] = await Promise.all([promoPromise, statusPromise]);
        if (cancelled) return;
        setPromo(promoResult.data || null);
        setAlreadyExempt(Boolean(status?.ownerExempt));
      } catch (error) {
        console.error('Founding lister promo load error:', error);
        if (!cancelled) setPromo(null);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (dismissed || alreadyExempt || !promo?.offerOpen) return null;

  const remaining = Number(promo.foundingSlotsRemaining) || 0;
  const cap = Number(promo.foundingListerCap) || FOUNDING_LISTER_CAP_DEFAULT;
  const listTo = user ? '/your-hangar' : '/create-profile';
  const slotsLabel =
    remaining === 1 ? '1 free-for-life spot left' : `${remaining} free-for-life spots left`;

  return (
    <aside
      className={`founding-lister-banner${compact ? ' founding-lister-banner-compact' : ''} ${className}`.trim()}
      aria-label="Founding free-for-life offer"
    >
      <div className="founding-lister-banner-inner">
        <div className="founding-lister-banner-copy">
          <p className="founding-lister-banner-kicker">Founding owner offer</p>
          <p className="founding-lister-banner-title">
            List gear. Keep <strong>100%</strong> for life.
          </p>
          <p className="founding-lister-banner-text">
            The first <strong>{cap}</strong> people who post a listing get free platform use forever — no 10% cut, we
            cover card fees too. Sign-ups alone do not count; you have to list.
          </p>
          <p className="founding-lister-banner-slots" role="status">
            {slotsLabel}
            {remaining <= 10 && remaining > 0 ? ' — going fast' : ''}
          </p>
        </div>
        <div className="founding-lister-banner-actions">
          <Link to={listTo} className="founding-lister-banner-cta">
            {user ? 'List in Your Hangar' : 'Create account & list'}
          </Link>
          <button
            type="button"
            className="founding-lister-banner-dismiss"
            onClick={() => setDismissed(true)}
            aria-label="Dismiss founding offer banner"
          >
            Not now
          </button>
        </div>
      </div>
    </aside>
  );
}

export default FoundingListerBanner;
